import { goalTypeSubject } from "@/shared/domain/agents";

import { type GoalState } from "../Agents/buildRequestTemplate";

const disjunction = new Intl.ListFormat("en", { type: "disjunction" });

/** One sentence on what this goal does, from its own vocabulary and settings. */
export function describeGoal(goal: GoalState): string {
  switch (goal.goal_type) {
    case "dataset_summary":
    case "session_summary":
      return isBlank(goal.summary_format_spec_prompt)
        ? `Writes up the ${goalTypeSubject(goal.goal_type)} in the default format.`
        : `Writes up the ${goalTypeSubject(goal.goal_type)} in a format you set.`;
    case "dataset_triage":
    case "session_triage": {
      const labels = goal.label_vocabulary.map((row) => row.label);
      if (labels.length === 0) {
        return "Applies a label from an empty vocabulary.";
      }
      if (labels.length === 1) {
        return `Applies the label ${labels[0]}.`;
      }
      return `Applies one of ${labels.length} labels: ${labels.join(", ")}.`;
    }
    case "create_events":
    case "session_create_events": {
      const events = goal.event_vocabulary.map((row) => row.name);
      const tags = goal.tag_vocabulary.map((row) => row.tag);
      const clauses = [
        events.length === 0
          ? "Creates events from an empty vocabulary"
          : `Creates ${disjunction.format(events)} events`,
      ];
      if (tags.length > 0) {
        clauses.push(`tagged ${disjunction.format(tags)}`);
      }
      if (goal.goal_type === "session_create_events") {
        clauses.push("on the session chosen at launch");
      } else if (goal.collection_enabled) {
        clauses.push("filed into a collection chosen at launch");
      }
      return `${clauses.join(", ")}.`;
    }
  }
}

/** A vocabulary entry: the word the agent may use, and when it applies. */
export interface GoalArtifactRow {
  key: string;
  description: string;
}

/**
 * One thing a goal carries beyond its kind, named by the goal field it shows.
 * A `line` has nothing of its own to show: it stands for a default or a flag.
 */
export type GoalArtifact =
  | {
      kind: "table";
      id: "label_vocabulary" | "event_vocabulary" | "tag_vocabulary";
      rows: GoalArtifactRow[];
    }
  | { kind: "quote"; id: "summary_format" | "event_focus"; text: string }
  | { kind: "line"; id: "summary_format" }
  | { kind: "line"; id: "collection" };

/**
 * What a goal carries, in the order its card on the form lays it out. A goal's
 * required vocabulary is listed even when empty; optional parts (tags, the
 * event focus, the collection) only once set.
 */
export function goalArtifacts(goal: GoalState): GoalArtifact[] {
  switch (goal.goal_type) {
    case "dataset_summary":
    case "session_summary":
      return [
        isBlank(goal.summary_format_spec_prompt)
          ? { kind: "line", id: "summary_format" }
          : {
              kind: "quote",
              id: "summary_format",
              text: goal.summary_format_spec_prompt,
            },
      ];
    case "dataset_triage":
    case "session_triage":
      return [
        {
          kind: "table",
          id: "label_vocabulary",
          rows: goal.label_vocabulary.map(({ label, description }) => ({
            key: label,
            description,
          })),
        },
      ];
    case "create_events":
    case "session_create_events": {
      const artifacts: GoalArtifact[] = [
        {
          kind: "table",
          id: "event_vocabulary",
          rows: goal.event_vocabulary.map(({ name, description }) => ({
            key: name,
            description,
          })),
        },
      ];
      if (goal.tag_vocabulary.length > 0) {
        artifacts.push({
          kind: "table",
          id: "tag_vocabulary",
          rows: goal.tag_vocabulary.map(({ tag, description }) => ({
            key: tag,
            description,
          })),
        });
      }
      if (goal.goal_type === "create_events" && goal.collection_enabled) {
        artifacts.push({ kind: "line", id: "collection" });
      }
      if (!isBlank(goal.event_focus_prompt)) {
        artifacts.push({
          kind: "quote",
          id: "event_focus",
          text: goal.event_focus_prompt,
        });
      }
      return artifacts;
    }
  }
}

function isBlank(text: string): boolean {
  return text.trim() === "";
}
